import {
  ListSalesExecutivesForAccountApiArg,
  useListSalesExecutivesForAccountQuery,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import type { SalesExecutive } from "../types";

export interface HookArgs {
  page?: ListSalesExecutivesForAccountApiArg["page"];
  filter?: ListSalesExecutivesForAccountApiArg["filter"];
}

const useListSalesExecutives = ({ page, filter }: HookArgs) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data, error, isLoading, refetch } =
    useListSalesExecutivesForAccountQuery({
      slug: accountSlug,
      page,
      filter,
    });

  let salesExecutives: SalesExecutive[] = [];
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  if (data?.data) {
    try {
      salesExecutives = data.data.map((se) => {
        if (!se.id) {
          throw new Error("Missing sales executive id");
        }

        return {
          id: se.id,
          name: se.attributes?.name || "",
          phone: se.attributes?.phone,
          email: se.attributes?.email,
          title: se.attributes?.title,
          active: se.attributes?.active,
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return {
    salesExecutives,
    error: error || customError,
    isLoading,
    pageMeta,
    refetch,
  };
};

export default useListSalesExecutives;
